import { useContext } from "react";
import { Button } from "@/modern/components/ui/button";
import { Construction, ExternalLink } from "lucide-react";
import BryntumContext from "@/react/context/bryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";

interface LegacySettingsRedirectProps {
  title: string;
  description?: string;
  categoryId?: string;
}

const LegacySettingsRedirect = ({
  title,
  description = "This feature is currently available in the legacy settings editor.",
  categoryId
}: LegacySettingsRedirectProps) => {
  const { editorHandler } = useContext(BryntumContext);

  // Determine if this is a personal or general setting based on category
  const isPersonalSetting = categoryId === "personal";

  const handleOpenLegacySettings = () => {
    const editorKey = isPersonalSetting
      ? EDITOR_KEYS.settingPersonalEditor
      : EDITOR_KEYS.settingGeneralEditor;

    editorHandler.openEditor(
      editorKey,
      null,
      {},
      { useExisting: true, by: "recordType" }
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {/* Coming Soon Card */}
      <div className="p-8 rounded-lg border border-border bg-muted/30 flex flex-col items-center justify-center text-center">
        <Construction className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Feature in Development</h3>
        <p className="text-sm text-muted-foreground max-w-md mb-6">
          This feature is being redesigned for a better experience. In the meantime, you can access these settings using the legacy editor.
        </p>

        <Button
          onClick={handleOpenLegacySettings}
          variant="outline"
          className="flex items-center gap-2"
        >
          <ExternalLink className="h-4 w-4" />
          Open Legacy {isPersonalSetting ? "Personal" : "Board"} Settings
        </Button>
      </div>

      {/* Information Note */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-900 dark:text-blue-100 leading-relaxed">
          <strong>Note:</strong> The legacy settings editor contains all available configuration options.
          We're working to bring these features to the new settings interface with improved usability.
        </p>
      </div>
    </div>
  );
};

export default LegacySettingsRedirect;
